/** Pass 6.0b.3 — helpers for Slack's reply_broadcast feature
 *  (subtype="thread_broadcast"). A broadcast message lives in
 *  BOTH the thread reply list AND the main channel/DM feed.
 *  The viewer detects them so the top-level row can render a
 *  "replied to a thread:" header with the parent's preview, the
 *  way Slack itself does. */

import type { ApiConversation, ApiMessage, ApiUser } from "./api";
import { formatSlackTextPlain } from "./slackMarkup";

/** True when ``msg`` is a thread_broadcast that should render the
 *  top-level header + parent preview. */
export function isReplyBroadcast(msg: ApiMessage): boolean {
  return (
    msg.subtype === "thread_broadcast"
    && typeof msg.thread_ts === "string"
    && msg.thread_ts !== msg.ts
  );
}

/** Pass 6.0c — substitute Slack markup (mentions, channels, links,
 *  broadcasts) to operator-readable plain text BEFORE collapsing
 *  whitespace + truncating. Without the substitution pass the
 *  preview rendered raw "<@U0AJ33NCCU9>" syntax — confusing for the
 *  operator who sees nicely-formatted mentions in Slack itself.
 *  This is a label, not an interactive surface, so no chips / links
 *  — plain @username text only. The renderer path (message body)
 *  still uses the rich slack-text.tsx renderer with mention chips.
 *
 *  ``users`` + ``conversations`` are optional. When absent (e.g.
 *  legacy callers that pre-date 6.0c), unknown user_ids fall back
 *  to "@unknown-user" rather than the raw <@ID> syntax — never
 *  expose raw Slack markup to the operator. */
export function previewParentText(
  text: string | undefined,
  users?: Record<string, ApiUser>,
  conversations?: ApiConversation[],
): string {
  if (!text) return "";
  const resolved = formatSlackTextPlain(text, users ?? {}, conversations ?? []);
  const oneLine = resolved.replace(/\s+/g, " ").trim();
  if (oneLine.length <= PREVIEW_MAX) return oneLine;
  return oneLine.slice(0, PREVIEW_MAX).trimEnd() + "…";
}

export const PREVIEW_MAX = 120;
